import { AdminApiError, type AdminClient, type DragontigerMultipliersOverride } from '@apx/admin-sdk';
import { dragontigerMultipliersSchema } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { classifyReturn } from './inheritance.js';

export interface DragontigerMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

const BPS_SCALE = 10000;

// Ordered bet types — mirrors DT_BET_TYPES in @apx/game-dragontiger. Kept local
// to avoid an engine→games dependency. Keep in sync if bet types change.
type DtReturnKey =
  | 'DT_DRAGON' | 'DT_TIGER' | 'DT_TIE'
  | 'DT_DRAGON_BLACK' | 'DT_DRAGON_RED' | 'DT_TIGER_BLACK' | 'DT_TIGER_RED'
  | 'DT_SUM_2_26' | 'DT_SUM_3_6' | 'DT_SUM_7_11'
  | 'DT_SUM_12_16' | 'DT_SUM_17_21' | 'DT_SUM_22_25';

const RETURN_KEYS: DtReturnKey[] = [
  'DT_DRAGON', 'DT_TIGER', 'DT_TIE',
  'DT_DRAGON_BLACK', 'DT_DRAGON_RED', 'DT_TIGER_BLACK', 'DT_TIGER_RED',
  'DT_SUM_2_26', 'DT_SUM_3_6', 'DT_SUM_7_11',
  'DT_SUM_12_16', 'DT_SUM_17_21', 'DT_SUM_22_25',
];

// Built-in total-return bps defaults — mirrors DEFAULT_DT_RETURN_BPS in
// @apx/game-dragontiger. Shown when no override exists yet. Exported so a parity
// test (multipliers-defaults.test.ts) can assert it never drifts from the
// package constant.
export const DT_DEFAULT_RETURNS: Record<DtReturnKey, number> = {
  DT_DRAGON: 20000,
  DT_TIGER: 20000,
  DT_TIE: 90000,
  DT_DRAGON_BLACK: 18000,
  DT_DRAGON_RED: 18000,
  DT_TIGER_BLACK: 18000,
  DT_TIGER_RED: 18000,
  DT_SUM_2_26: 790000,
  DT_SUM_3_6: 110000,
  DT_SUM_7_11: 38000,
  DT_SUM_12_16: 26000,
  DT_SUM_17_21: 38000,
  DT_SUM_22_25: 110000,
};

const DEFAULT_TIE_REFUND_BPS = 5000;
const DEFAULT_MAX_RETURN_BPS = 15_600_000;

const DEFAULT_VALUE: DragontigerMultipliersOverride = {
  version: 1,
  returns: { ...DT_DEFAULT_RETURNS },
  tieRefundBps: DEFAULT_TIE_REFUND_BPS,
  maxReturnBps: DEFAULT_MAX_RETURN_BPS,
};

/** Human hint: total-return bps → "x.xx× return (n:1)". */
function returnHint(bps: number): string {
  const ret = bps / BPS_SCALE;
  const odds = (bps - BPS_SCALE) / BPS_SCALE;
  return `${ret.toFixed(2)}× return (${odds.toFixed(2)}:1)`;
}

function rowHtml(key: DtReturnKey, isIntegratorScope: boolean): string {
  const tagCell = isIntegratorScope
    ? `<td data-tag="${key}" class="muted" style="min-width:80px">—</td>`
    : `<td class="muted" data-default="${key}">default ${DT_DEFAULT_RETURNS[key]} bps</td>`;
  const revertCell = isIntegratorScope
    ? `<td><button data-revert="${key}" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button></td>`
    : `<td></td>`;
  return `<tr data-row="${key}" data-source="inherited">
    <td><strong>${key.replace(/^DT_/, '')}</strong></td>
    <td><input data-bet="${key}" type="number" min="0" max="100000000" step="1" style="width:110px" /></td>
    <td class="muted" data-hint="${key}">—</td>
    ${tagCell}
    ${revertCell}
  </tr>`;
}

/**
 * Dragon & Tiger payout-multiplier editor. One total-return-bps input per bet
 * type (`returns`), plus the `tieRefundBps` lever (refund on D/T main bets when
 * the round ties) and `maxReturnBps` ceiling (crit-capped payout cap). Values
 * are total-return basis points: 20000 = 2.0× return = 1:1 win. Changes ride the
 * two-admin approval flow. Live RTP (real settled rounds) shown for sanity.
 *
 * In integrator scope: rows show inherited/override tags and per-row revert.
 * readForm() only includes override keys in the returned `returns` map.
 * In provider scope: full table submitted, no inheritance UI.
 */
export async function renderDragontigerMultipliersPage(
  root: HTMLElement,
  deps: DragontigerMultipliersPageDeps,
): Promise<void> {
  const isIntegratorScope = !deps.isProviderScope;

  const resetAllBtn = isIntegratorScope
    ? `<button id="dm-reset-all" class="ghost">Reset all to provider default</button>`
    : '';

  const scalarTagHtml = (id: string) =>
    isIntegratorScope
      ? `<span id="${id}-tag" style="font-size:12px;font-weight:600;color:var(--muted)">—</span>
         <button id="${id}-revert" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button>`
      : '';

  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — Dragon &amp; Tiger</h1>
    <p class="page-sub">Total-return per unit stake in basis points: <code>20000 = 2.0× return = 1:1 win</code>. Tie refund and max-return ceiling apply on top. Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Refund &amp; ceiling</h2>
      <div class="form-grid">
        <label>tieRefundBps
          <input id="dm-tie" type="number" min="0" max="100000000" step="1" />
          <span class="muted" id="dm-tie-hint">—</span>
          ${scalarTagHtml('dm-tie')}
        </label>
        <label>maxReturnBps
          <input id="dm-max" type="number" min="0" max="100000000" step="1" />
          <span class="muted" id="dm-max-hint">—</span>
          ${scalarTagHtml('dm-max')}
        </label>
      </div>
      <p class="muted" style="margin-top:8px">Tie refund returns a fraction of stake on Dragon/Tiger main bets when the round ties (<code>5000 = 0.5×</code>). Max return caps any single crit-boosted payout.</p>
    </div>

    <div class="card">
      <h2>Per-bet-type return (bps)</h2>
      <table>
        <thead><tr><th>Bet type</th><th>Return (bps)</th><th>Multiple</th>${isIntegratorScope ? '<th>Source</th><th></th>' : '<th></th><th></th>'}</tr></thead>
        <tbody id="dm-rows">${RETURN_KEYS.map((k) => rowHtml(k, isIntegratorScope)).join('')}</tbody>
      </table>
      <div class="row" style="margin-top:14px">
        <button id="dm-reset" class="ghost">Reset to defaults</button>
        ${resetAllBtn}
        <button id="dm-save" class="primary">Propose change</button>
        <span id="dm-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="dm-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rowsEl = root.querySelector<HTMLElement>('#dm-rows')!;
  const tieEl = root.querySelector<HTMLInputElement>('#dm-tie')!;
  const maxEl = root.querySelector<HTMLInputElement>('#dm-max')!;
  const tieHintEl = root.querySelector<HTMLElement>('#dm-tie-hint')!;
  const maxHintEl = root.querySelector<HTMLElement>('#dm-max-hint')!;
  const tieTagEl = root.querySelector<HTMLElement>('#dm-tie-tag');
  const tieRevertEl = root.querySelector<HTMLButtonElement>('#dm-tie-revert');
  const maxTagEl = root.querySelector<HTMLElement>('#dm-max-tag');
  const maxRevertEl = root.querySelector<HTMLButtonElement>('#dm-max-revert');
  const msgEl = root.querySelector<HTMLElement>('#dm-msg')!;

  // Per-row inherited values for revert (integrator scope only)
  const inheritedValues = new Map<DtReturnKey, number>();
  let inheritedTieBps = DEFAULT_TIE_REFUND_BPS;
  let inheritedMaxBps = DEFAULT_MAX_RETURN_BPS;
  let tieIsOverride = false;
  let maxIsOverride = false;

  const inputs = new Map<DtReturnKey, HTMLInputElement>();
  for (const key of RETURN_KEYS) {
    const el = rowsEl.querySelector<HTMLInputElement>(`input[data-bet="${key}"]`);
    if (el) {
      inputs.set(key, el);
      el.addEventListener('input', () => updateHint(key));
    }
  }

  function updateHint(key: DtReturnKey): void {
    const hintEl = rowsEl.querySelector<HTMLElement>(`[data-hint="${key}"]`);
    if (hintEl) hintEl.textContent = returnHint(Number(inputs.get(key)?.value) || 0);
  }

  tieEl.addEventListener('input', () => {
    tieHintEl.textContent = `${((Number(tieEl.value) || 0) / BPS_SCALE).toFixed(2)}× stake refunded on tie`;
  });
  maxEl.addEventListener('input', () => {
    maxHintEl.textContent = `${((Number(maxEl.value) || 0) / BPS_SCALE).toFixed(2)}× max return`;
  });

  /** Mark a row as override (integrator scope only). */
  function markRowOverride(key: DtReturnKey): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    if (row) row.dataset.source = 'override';
    if (tagEl) {
      tagEl.textContent = 'override';
      tagEl.style.color = '#f5c542';
      tagEl.style.fontWeight = '600';
    }
    if (revertEl) revertEl.style.display = '';
  }

  /** Mark a row as inherited (integrator scope only). */
  function markRowInherited(key: DtReturnKey): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    if (row) row.dataset.source = 'inherited';
    if (tagEl) {
      tagEl.textContent = 'inherited';
      tagEl.style.color = 'var(--muted)';
      tagEl.style.fontWeight = '';
    }
    if (revertEl) revertEl.style.display = 'none';
  }

  function markScalarOverride(tagEl: HTMLElement | null, revertEl: HTMLButtonElement | null): void {
    if (tagEl) {
      tagEl.textContent = 'override';
      tagEl.style.color = '#f5c542';
      tagEl.style.fontWeight = '600';
    }
    if (revertEl) revertEl.style.display = '';
  }

  function markScalarInherited(tagEl: HTMLElement | null, revertEl: HTMLButtonElement | null): void {
    if (tagEl) {
      tagEl.textContent = 'inherited';
      tagEl.style.color = 'var(--muted)';
      tagEl.style.fontWeight = '';
    }
    if (revertEl) revertEl.style.display = 'none';
  }

  /** Seed rows from a full override value (provider scope or reset). */
  function fillForm(value: DragontigerMultipliersOverride): void {
    const returns = (value.returns ?? {}) as Record<string, number>;
    for (const [key, input] of inputs) {
      input.value = String(returns[key] ?? DT_DEFAULT_RETURNS[key]);
      updateHint(key);
    }
    tieEl.value = String(value.tieRefundBps ?? DEFAULT_TIE_REFUND_BPS);
    maxEl.value = String(value.maxReturnBps ?? DEFAULT_MAX_RETURN_BPS);
    tieEl.dispatchEvent(new Event('input'));
    maxEl.dispatchEvent(new Event('input'));
  }

  /** Seed rows using inheritance classification (integrator scope). */
  function fillFormInherited(
    ownReturns: Record<string, number> | undefined,
    providerReturns: Record<string, number> | undefined,
    ownTieBps: number | undefined,
    providerTieBps: number | undefined,
    ownMaxBps: number | undefined,
    providerMaxBps: number | undefined,
  ): void {
    for (const [key, input] of inputs) {
      const classified = classifyReturn(key, ownReturns, providerReturns, DT_DEFAULT_RETURNS[key]);
      input.value = String(classified.value);
      const inheritedVal =
        providerReturns && Object.prototype.hasOwnProperty.call(providerReturns, key)
          ? providerReturns[key]!
          : DT_DEFAULT_RETURNS[key];
      inheritedValues.set(key, inheritedVal);
      updateHint(key);
      if (classified.source === 'override') {
        markRowOverride(key);
      } else {
        markRowInherited(key);
      }
    }

    // tieRefundBps scalar
    const effectiveTie =
      ownTieBps !== undefined
        ? ownTieBps
        : providerTieBps !== undefined
          ? providerTieBps
          : DEFAULT_TIE_REFUND_BPS;
    inheritedTieBps = providerTieBps !== undefined ? providerTieBps : DEFAULT_TIE_REFUND_BPS;
    tieEl.value = String(effectiveTie);
    tieEl.dispatchEvent(new Event('input'));
    if (ownTieBps !== undefined) {
      tieIsOverride = true;
      markScalarOverride(tieTagEl, tieRevertEl);
    } else {
      tieIsOverride = false;
      markScalarInherited(tieTagEl, tieRevertEl);
    }

    // maxReturnBps scalar
    const effectiveMax =
      ownMaxBps !== undefined
        ? ownMaxBps
        : providerMaxBps !== undefined
          ? providerMaxBps
          : DEFAULT_MAX_RETURN_BPS;
    inheritedMaxBps = providerMaxBps !== undefined ? providerMaxBps : DEFAULT_MAX_RETURN_BPS;
    maxEl.value = String(effectiveMax);
    maxEl.dispatchEvent(new Event('input'));
    if (ownMaxBps !== undefined) {
      maxIsOverride = true;
      markScalarOverride(maxTagEl, maxRevertEl);
    } else {
      maxIsOverride = false;
      markScalarInherited(maxTagEl, maxRevertEl);
    }
  }

  /** Build the propose payload. In integrator scope, only includes override keys. */
  function readForm(): DragontigerMultipliersOverride {
    if (deps.isProviderScope) {
      const returns: Record<string, number> = {};
      for (const [key, input] of inputs) returns[key] = Math.round(Number(input.value) || 0);
      return {
        version: 1,
        returns,
        tieRefundBps: Math.round(Number(tieEl.value) || 0),
        maxReturnBps: Math.round(Number(maxEl.value) || 0),
      } as DragontigerMultipliersOverride;
    }
    // Integrator scope: partial returns — only keys marked override
    const returns: Record<string, number> = {};
    for (const [key, input] of inputs) {
      const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
      if (row?.dataset.source === 'override') {
        returns[key] = Math.round(Number(input.value) || 0);
      }
    }
    return {
      version: 1,
      returns,
      tieRefundBps: tieIsOverride ? Math.round(Number(tieEl.value) || 0) : inheritedTieBps,
      maxReturnBps: maxIsOverride ? Math.round(Number(maxEl.value) || 0) : inheritedMaxBps,
    } as DragontigerMultipliersOverride;
  }

  // Wire per-row input → mark override on edit (integrator scope only)
  if (isIntegratorScope) {
    for (const [key, input] of inputs) {
      input.addEventListener('input', () => {
        markRowOverride(key);
        updateHint(key);
      });
    }
    tieEl.addEventListener('input', () => {
      tieIsOverride = true;
      markScalarOverride(tieTagEl, tieRevertEl);
    });
    maxEl.addEventListener('input', () => {
      maxIsOverride = true;
      markScalarOverride(maxTagEl, maxRevertEl);
    });

    // Per-row revert buttons
    for (const key of RETURN_KEYS) {
      const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
      if (revertEl) {
        revertEl.addEventListener('click', () => {
          const inherited = inheritedValues.get(key) ?? DT_DEFAULT_RETURNS[key];
          const input = inputs.get(key);
          if (input) {
            input.value = String(inherited);
            updateHint(key);
          }
          markRowInherited(key);
        });
      }
    }

    // Scalar revert — tie
    if (tieRevertEl) {
      tieRevertEl.addEventListener('click', () => {
        tieEl.value = String(inheritedTieBps);
        tieEl.dispatchEvent(new Event('input'));
        tieIsOverride = false;
        markScalarInherited(tieTagEl, tieRevertEl);
      });
    }

    // Scalar revert — max
    if (maxRevertEl) {
      maxRevertEl.addEventListener('click', () => {
        maxEl.value = String(inheritedMaxBps);
        maxEl.dispatchEvent(new Event('input'));
        maxIsOverride = false;
        markScalarInherited(maxTagEl, maxRevertEl);
      });
    }

    // Reset all to provider default
    const resetAllEl = root.querySelector<HTMLButtonElement>('#dm-reset-all');
    if (resetAllEl) {
      resetAllEl.addEventListener('click', () => {
        for (const [key, input] of inputs) {
          const inherited = inheritedValues.get(key) ?? DT_DEFAULT_RETURNS[key];
          input.value = String(inherited);
          updateHint(key);
          markRowInherited(key);
        }
        tieEl.value = String(inheritedTieBps);
        tieEl.dispatchEvent(new Event('input'));
        tieIsOverride = false;
        markScalarInherited(tieTagEl, tieRevertEl);
        maxEl.value = String(inheritedMaxBps);
        maxEl.dispatchEvent(new Event('input'));
        maxIsOverride = false;
        markScalarInherited(maxTagEl, maxRevertEl);
        msgEl.textContent = 'All rows reset to provider/baked defaults (not saved).';
        msgEl.className = 'muted';
      });
    }
  }

  // Load current data
  if (isIntegratorScope) {
    const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
    const ownMult = inh.ownOverride.multipliers as Partial<DragontigerMultipliersOverride> | undefined;
    const provMult = inh.providerDefault.multipliers as Partial<DragontigerMultipliersOverride> | undefined;
    fillFormInherited(
      ownMult?.returns as Record<string, number> | undefined,
      provMult?.returns as Record<string, number> | undefined,
      ownMult?.tieRefundBps,
      provMult?.tieRefundBps,
      ownMult?.maxReturnBps,
      provMult?.maxReturnBps,
    );
  } else {
    const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
    const stored = current as Partial<DragontigerMultipliersOverride> | null;
    fillForm(stored && stored.returns ? (stored as DragontigerMultipliersOverride) : DEFAULT_VALUE);
  }

  root.querySelector<HTMLButtonElement>('#dm-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    if (isIntegratorScope) {
      for (const key of RETURN_KEYS) markRowOverride(key);
      tieIsOverride = true;
      markScalarOverride(tieTagEl, tieRevertEl);
      maxIsOverride = true;
      markScalarOverride(maxTagEl, maxRevertEl);
    }
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#dm-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = dragontigerMultipliersSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data as DragontigerMultipliersOverride,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#dm-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — payout multipliers now live.';
          msgEl.className = 'muted';
          await refreshPending();
          if (isIntegratorScope) {
            const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
            const ownMult = inh.ownOverride.multipliers as Partial<DragontigerMultipliersOverride> | undefined;
            const provMult = inh.providerDefault.multipliers as Partial<DragontigerMultipliersOverride> | undefined;
            fillFormInherited(
              ownMult?.returns as Record<string, number> | undefined,
              provMult?.returns as Record<string, number> | undefined,
              ownMult?.tieRefundBps,
              provMult?.tieRefundBps,
              ownMult?.maxReturnBps,
              provMult?.maxReturnBps,
            );
          } else {
            const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
            const value = fresh.value as Partial<DragontigerMultipliersOverride> | null;
            fillForm(value && value.returns ? (value as DragontigerMultipliersOverride) : DEFAULT_VALUE);
          }
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
  }
  await refreshPending();
}
